import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Archive, Delete, Edit } from '@material-ui/icons';

import PropTypes from 'prop-types';

import CustomCheckBox from '../../fields/customCheckBox';
import DeleteToolBar from '../../fields/deleteToolBar';
import SearchField from '../../fields/searchField';
import TablePaginationActions from '../../fields/tablePaginationActions';

import { filteredSearch, getComparator, getDataSheet, stableSort } from '../../../utils/helpers';

import { defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions } from '../../../utils/constants';

import translations from '../../../languages/i18n';

import fieldStyles from '../../../styles/fieldStyles';
import { customOptionsTableStyles } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

export default function CliniciansListBasic({ cliniciansData, handleViewClinician, handleDelete, handleSelectedCliniciansData, isProgramAdministrator, selectedClinicians }) {
  const classesField = fieldStyles();
  const classesForm = customOptionsTableStyles();

  const sessionContext = useContext(SessionContext);

  const [filteredUsers, setFilteredUsers] = useState(cliniciansData.slice());
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('username'); // Indica la columna per la que ordenam per defecte
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    setFilteredUsers(cliniciansData.slice());
    setSearchString('');
  }, [cliniciansData]);

  // Comprovam si el clínic del llistat administra el programa
  const administersProgram = (program, clinicianId) => {
    return program.accessibleBy.clinicians.find((clinician) => clinician.clinician !== null && clinician.clinician._id === clinicianId && clinician.admin === true);
  };

  const clinicianProgramsList = (programsArray, clinicianId) => {
    return (
      <React.Fragment>
        {programsArray.map((program, index) => (
          <Typography key={`cliProLis_${clinicianId}_${index}`} variant='body2'>
            {sessionContext.userId === clinicianId ? (
              <strong>                
                {(administersProgram(program, clinicianId) && (translations[program.sharedId] ? translations[program.sharedId].toUpperCase() : program.name.toUpperCase())) || (translations[program.sharedId] || program.name)}
              </strong>
            ) : (
              (administersProgram(program, clinicianId) && (translations[program.sharedId] ? translations[program.sharedId].toUpperCase() : program.name.toUpperCase())) || (translations[program.sharedId] || program.name)
            )}
            <br />
          </Typography>
        ))}
      </React.Fragment>
    );
  };

  const handleCheck = (id, username) => {
    handleSelectedCliniciansData(id, username);
  };

  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const searchClinician = (string) => {
    setPage(0);
    setSearchString(string);
    setFilteredUsers(filteredSearch(cliniciansData, string, ['username', 'firstName', 'lastName', 'programs']));
  };

  // A part d'anar alternant el color de fons de les files,
  // destacam amb un altre color el fons de la fila corresponent a l'usuari clínic que està consultant la web
  const customBgStyle = (index, user) => {
    if (sessionContext.username === user) return classesForm.tableRowMarked;
    if (index % 2) return classesForm.tableRowBgColor1;
    return classesForm.tableRowBgColor2;
  };

  const cliniciansDataColumns = 4; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='clinicians list table'>
        <TableHead>
          <TableRow>
            {/* ATENCIÓ: si pintam la columna _id afegir 1 al colSpan */}
            <TableCell colSpan={cliniciansDataColumns + 2} align='left' padding='none'>
              {(selectedClinicians.length > 0 ? (
                <DeleteToolBar numSelected={selectedClinicians.length} handleClick={handleDelete} />
              ) : (
                <Grid container direction='row' alignItems='center' justify='flex-start'>
                  <Grid item>
                    <SearchField handleChange={(string) => searchClinician(string)} value={searchString} />
                  </Grid>
                  <Grid item>
                    <IconButton style={{marginLeft: 60}} size='medium' onClick={() => getDataSheet(filteredUsers, ['username', 'firstName', 'lastName', 'programs'])}>
                      <Archive />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={classesForm.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'username'}
                direction={orderBy === 'username' ? order : 'asc'}
                onClick={() => handleRequestSort('username')}
              >
                <strong>{translations['_username']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'firstName'}
                direction={orderBy === 'firstName' ? order : 'asc'}
                onClick={() => handleRequestSort('firstName')}
              >
                <strong>{translations['_firstName']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'lastName'}
                direction={orderBy === 'lastName' ? order : 'asc'}
                onClick={() => handleRequestSort('lastName')}
              >
                <strong>{translations['_lastName']}</strong>
              </TableSortLabel>
            </TableCell>
            {/* <TableCell align='left'><strong>{translations['_email']}</strong></TableCell> */}
            <TableCell align='left'><strong>{translations['_programs']}</strong></TableCell>
            <TableCell align='center' />{/* Columna per al botó d'editar */}
            {/* <TableCell align='center'>{sessionContext.role === 'Admin' && <Delete className={classesField.iconBody} />}</TableCell> */}
            <TableCell align='center'><Delete className={classesField.iconBody} /></TableCell>
          </TableRow>
          {(rowsPerPage > 0
            // ATENCIÓ: l'ordenació es fa damunt filteredSchedules, si no s'ha introduit res al camp de cerca el filteredSchedules inclou tot el schedulesData,
            // és a dir, l'ordenació no es fa sobre la "pagina de formularis" que es mostra al llistat (estigui filtrada o no)
            ? stableSort(filteredUsers, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(filteredUsers, getComparator(order, orderBy))
          ).map((data, index) => (
            <TableRow key={data._id} className={customBgStyle(index, data.username)} hover>
              {/* <TableRow key={data._id} className={index % 2 ? classesForm.tableRowBgColor1 : classesForm.tableRowBgColor2} hover> */}
              {/* ATENCIÓ: no pintam la columna _id */}
              {/* Només un Admin pot editar/veure la info d'un clínic */}
              {/* Destacam en negreta la fila corresponent a l'usuari clí­nic que està  accedint a la web */}
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.username}</strong> : data.username}
              </TableCell>
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.firstName}</strong> : data.firstName}
              </TableCell>
              <TableCell align='left'>
                {sessionContext.username === data.username ? <strong>{data.lastName}</strong> : data.lastName}
              </TableCell>
              <TableCell align='left'>
                {/* sessionContext.username === data.username ? <strong>{data.email}</strong> : data.email */}
                {/* Si data.programs és null vol dir que s'ha produit algun error en obtenir els programes al component pare (ListClinicians) */}
                {data.programs !== null ? clinicianProgramsList(data.programs, data._id) : sessionContext.username === data.username ? <strong>{translations['_infoNotAvailable']}</strong> : translations['_infoNotAvailable']}
              </TableCell>
              <TableCell align='center'>
                <IconButton onClick={() => handleViewClinician(data._id)} disabled={!(sessionContext.role === 'Admin' || isProgramAdministrator)}>
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell align='center'>
                <CustomCheckBox
                  checked={selectedClinicians.findIndex((item) => item.id === data._id) > -1} // Si res.value es dins la taula la condició dona true
                  onClick={() => handleCheck(data._id, data.username)}
                  disabled={sessionContext.role !== 'Admin'} // Només l'administrador pot eliminar clínics
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={cliniciansDataColumns + 2}
              count={filteredUsers.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
CliniciansListBasic.propTypes = {
  cliniciansData: PropTypes.array.isRequired,
  handleViewClinician: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSelectedCliniciansData: PropTypes.func.isRequired,
  isProgramAdministrator: PropTypes.bool.isRequired,
  selectedClinicians: PropTypes.array.isRequired,
};
